import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchUrithiPurchases, deleteUrithiPurchase } from './hooks/hooks';
import { Search, Trash2, Hash } from 'lucide-react';
import {
  Toast, LoadingState, ErrorState, EmptyState, Pagination,
  ConfirmModal, StatusBadge,
} from './ui';

function UrithiPurchases() {
  const queryClient = useQueryClient();
  const { data: purchases, isLoading, error, refetch } = useQuery({
    queryKey: ['purchases'],
    queryFn: fetchUrithiPurchases,
  });

  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null });
  const [toast, setToast] = useState({ open: false, message: '', severity: '' });

  const deleteMutation = useMutation({
    mutationFn: deleteUrithiPurchase,
    onSuccess: () => {
      queryClient.invalidateQueries(['purchases']);
      setDeleteConfirm({ open: false, id: null });
      setToast({ open: true, message: 'Purchase deleted successfully', severity: 'success' });
    },
    onError: () => setToast({ open: true, message: 'Failed to delete purchase', severity: 'error' }),
  });

  const filteredAssets = useMemo(() =>
    Array.isArray(purchases)
      ? purchases.filter((item) =>
          item.asset?.asset_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.user?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.id?.toString().includes(searchQuery.toLowerCase())
        )
      : []
  , [purchases, searchQuery]);

  const formatCurrency = (n) => new Intl.NumberFormat('en-TZ').format(Number(n) || 0);

  if (isLoading) return <LoadingState message="Loading purchases…" />;
  if (error) return <ErrorState onRetry={refetch} />;
  if (purchases?.length === 0) {
    return <EmptyState title="No Purchases Yet" message="Purchase records will appear here once customers make purchases." />;
  }

  return (
    <div>
      <Toast {...toast} onClose={() => setToast({ open: false, message: '', severity: '' })} />
      <ConfirmModal
        open={deleteConfirm.open}
        title="Delete Purchase"
        message="Are you sure you want to delete this purchase? This action cannot be undone."
        confirmLabel="Yes, Delete"
        danger
        loading={deleteMutation.isLoading}
        onCancel={() => setDeleteConfirm({ open: false, id: null })}
        onConfirm={() => deleteMutation.mutate(deleteConfirm.id)}
      />

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-bold text-[#001f3f] uppercase tracking-wide">Purchases Overview</h2>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#001f3f]/10 text-[#001f3f] text-[10px] font-bold uppercase tracking-wide">
                {filteredAssets.length} {filteredAssets.length === 1 ? 'record' : 'records'}
              </span>
            </div>

            <div className="relative">
              <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
              <input type="text" placeholder="Search by asset, client, or ID…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-[#001f3f] focus:ring-2 focus:ring-[#001f3f]/10 w-full sm:w-72 bg-gray-50" />
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                {['Purchase', 'Asset', 'Client', 'Details', 'Payment Plan', 'Status', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] font-bold text-gray-600 uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredAssets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((asset) => (
                <tr key={asset.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 rounded-xl bg-[#001f3f]/10 flex items-center justify-center">
                        <Hash className="w-4 h-4 text-[#001f3f]" />
                      </div>
                      <p className="text-xs font-bold text-[#001f3f]">#{asset.id}</p>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <p className="text-xs font-bold text-[#001f3f] truncate max-w-[160px]">{asset.asset?.asset_name}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Worth: <span className="font-semibold text-emerald-600">TZS {formatCurrency(asset.asset?.asset_worth)}</span>
                    </p>
                    <p className="text-[11px] text-gray-500">Qty: <span className="font-semibold">{asset.quantity}</span></p>
                  </td>

                  <td className="px-4 py-3">
                    <p className="text-xs font-bold text-[#001f3f]">{asset.user?.name}</p>
                    <p className="text-[11px] text-gray-500 truncate max-w-[180px]">{asset.user?.email}</p>
                  </td>

                  <td className="px-4 py-3">
                    <p className="text-[11px] text-gray-600">
                      Total: <span className="font-bold text-blue-600">TZS {formatCurrency(asset.total_price)}</span>
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">{asset.duration_in_years} yrs · {asset.number_of_installments} installments</p>
                  </td>

                  <td className="px-4 py-3">
                    <p className="text-[11px] text-gray-600">
                      Per: <span className="font-bold text-purple-600">TZS {formatCurrency(asset.pay_per_installment)}</span>
                    </p>
                    <p className="text-[11px] text-gray-500 mt-0.5">
                      Net: <span className="font-semibold text-emerald-600">TZS {formatCurrency(asset.net_pay_per_installment)}</span>
                    </p>
                  </td>

                  <td className="px-4 py-3">
                    <StatusBadge status={asset.status} />
                  </td>

                  <td className="px-4 py-3">
                    <button onClick={() => setDeleteConfirm({ open: true, id: asset.id })} className="p-2 rounded-lg text-red-600 hover:bg-red-50 transition-colors" title="Delete">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} rowsPerPage={rowsPerPage} total={filteredAssets.length} onPageChange={setPage} onRowsPerPageChange={(r) => { setRowsPerPage(r); setPage(0); }} />
      </div>
    </div>
  );
}

export default UrithiPurchases;